import { UseQuery } from '@reduxjs/toolkit/dist/query/react/buildHooks'
import { BaseQueryFn, createApi, QueryDefinition, TypedUseQueryHookResult } from '@reduxjs/toolkit/query/react'
import { AxiosError, AxiosRequestConfig } from 'axios'
import indexedDBStorage from 'redux-persist-indexeddb-storage'

import autoMergeLevel2 from './persistScope/autoMergeLevel2'
import persistScopedReducer from './persistScope/persistScopedReducer'
import { actionAlertHandler, actionStyles } from '../common/constants/reduxConstants'
import {
  axiosAuthInstance,
  axiosCalendarServicInstance,
  axiosGenAIInstance,
  axiosGenericInstance,
} from '../common/http/axiosInstance'
import { QuerySubStateWithPersist } from './apiPersist/types/QuerySubStateWithPersist'

export enum RTKQueryTag {
  AI_DRAFT = 'AI_DRAFT',
  APPOINTMENT = 'APPOINTMENT',
  ASSIGNMENT = 'ASSIGNMENT',
  AVAILABLE_CALENDARS = 'AVAILABLE_CALENDARS',
  CARE_PROGRESS_SUMMARY = 'CARE_PROGRESS_SUMMARY',
  CALENDAR_CONFIGURATIONS = 'CALENDAR_CONFIGURATIONS',
  CALENDAR_PROVIDER = 'CALENDAR_PROVIDER',
  CLIENT = 'CLIENT',
  CONCURRENT_CARE = 'CONCURRENT_CARE',
  CONVERSATION = 'CONVERSATION',
  PROVIDER_PAIR_CONVERSATION = 'PROVIDER_PAIR_CONVERSATION',
  DRAFT = 'DRAFT',
  EPISODE = 'EPISODE',
  EVENTS_TEMPLATE = 'EVENTS_TEMPLATE',
  FEEDBACK = 'FEEDBACK',
  FLEXIBLE_ASSESSMENTS = 'FLEXIBLE_ASSESSMENTS',
  INDIVIDUAL_CLIENT = 'INDIVIDUAL_CLIENT',
  DOCUMENT = 'DOCUMENT',
  MEDICATIONS = 'MEDICATIONS',
  NEW_CLIENTS_LIST = 'NEW_CLIENTS_LIST',
  NOTE = 'NOTE',
  NP_TEST_PLAN = 'NP_TEST_PLAN',
  PATIENT_UPCOMING_APPOINTMENTS = 'PATIENT_UPCOMING_APPOINTMENTS',
  PERSIST = 'PERSIST',
  LYRA_AI_SUMMARY = 'LYRA_AI_SUMMARY',
  TEMPLATE = 'TEMPLATE',
  DOCUMENT_TEMPLATE = 'DOCUMENT_TEMPLATE',
  PROVIDER_TASK = 'PROVIDER_TASK',
  TSL_LETTER = 'TSL_LETTER',
  RISK_FLAG = 'RISK_FLAG',
  SAFETY_PLAN = 'SAFETY_PLAN',
  TRANSFER = 'TRANSFER',
  VIDEO_SESSION = 'VIDEO_SESSION',
  VIDEO_SESSION_PREFERENCE = 'VIDEO_SESSION_PREFERENCE',
  WEB_KEY = 'WEB_KEY',
}

// Client and provider-pair conversation lists are cached separately; invalidate both when either may have changed.
export const ALL_CONVERSATION_TAGS = [RTKQueryTag.CONVERSATION, RTKQueryTag.PROVIDER_PAIR_CONVERSATION]

export enum AxiosInstance {
  AUTH = 'AUTH',
  GENERIC = 'GENERIC',
  CALENDAR = 'CALENDAR',
  GENAI = 'GENAI',
}

const getAxiosInstance = (instance: AxiosInstance) => {
  switch (instance) {
    case AxiosInstance.AUTH:
      return axiosAuthInstance
    case AxiosInstance.GENERIC:
      return axiosGenericInstance
    case AxiosInstance.CALENDAR:
      return axiosCalendarServicInstance
    case AxiosInstance.GENAI:
      return axiosGenAIInstance
  }
}

const axiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string
      method?: AxiosRequestConfig['method']
      data?: AxiosRequestConfig['data']
      params?: AxiosRequestConfig['params']
      headers?: AxiosRequestConfig['headers']
      axiosInstanceType?: AxiosInstance
      withCredentials?: boolean
      showAlert?: boolean
      ignore404?: boolean
      responseType?: AxiosRequestConfig['responseType']
    },
    unknown,
    unknown
  > =>
  async (
    {
      url,
      method,
      data,
      params,
      headers,
      axiosInstanceType = AxiosInstance.AUTH,
      withCredentials = false,
      showAlert = true,
      ignore404 = false,
      responseType,
    },
    api,
  ) => {
    try {
      const axiosInstance = getAxiosInstance(axiosInstanceType)
      const result = await axiosInstance({
        url,
        method,
        data,
        params,
        headers,
        withCredentials,
        responseType,
      })
      return { data: result.data }
    } catch (axiosError) {
      const error = axiosError as AxiosError
      if (showAlert && (!ignore404 || error.response?.status !== 404)) {
        actionAlertHandler({ actionStyle: actionStyles.ERROR, message: error, dispatch: api.dispatch })
      }
      return {
        error,
      }
    }
  }

const baseQuery = axiosBaseQuery()
export const baseApi = createApi({
  baseQuery: baseQuery,
  endpoints: () => ({}),
  refetchOnMountOrArgChange: 60 * 20,
  tagTypes: [
    RTKQueryTag.AI_DRAFT,
    RTKQueryTag.APPOINTMENT,
    RTKQueryTag.ASSIGNMENT,
    RTKQueryTag.CARE_PROGRESS_SUMMARY,
    RTKQueryTag.AVAILABLE_CALENDARS,
    RTKQueryTag.CALENDAR_CONFIGURATIONS,
    RTKQueryTag.CALENDAR_PROVIDER,
    RTKQueryTag.CLIENT,
    RTKQueryTag.CONCURRENT_CARE,
    RTKQueryTag.CONVERSATION,
    RTKQueryTag.PROVIDER_PAIR_CONVERSATION,
    RTKQueryTag.DRAFT,
    RTKQueryTag.EPISODE,
    RTKQueryTag.EVENTS_TEMPLATE,
    RTKQueryTag.FEEDBACK,
    RTKQueryTag.FLEXIBLE_ASSESSMENTS,
    RTKQueryTag.INDIVIDUAL_CLIENT,
    RTKQueryTag.DOCUMENT,
    RTKQueryTag.MEDICATIONS,
    RTKQueryTag.NOTE,
    RTKQueryTag.NP_TEST_PLAN,
    RTKQueryTag.NEW_CLIENTS_LIST,
    RTKQueryTag.PATIENT_UPCOMING_APPOINTMENTS,
    RTKQueryTag.PERSIST,
    RTKQueryTag.LYRA_AI_SUMMARY,
    RTKQueryTag.TEMPLATE,
    RTKQueryTag.DOCUMENT_TEMPLATE,
    RTKQueryTag.PROVIDER_TASK,
    RTKQueryTag.TSL_LETTER,
    RTKQueryTag.RISK_FLAG,
    RTKQueryTag.SAFETY_PLAN,
    RTKQueryTag.TRANSFER,
    RTKQueryTag.VIDEO_SESSION,
    RTKQueryTag.VIDEO_SESSION_PREFERENCE,
    RTKQueryTag.WEB_KEY,
  ],
})

export type ExtractRTKQueryArgs<T> = T extends UseQuery<QueryDefinition<infer Args, any, any, any, any>> ? Args : never
type ExtractRTKQueryReturnType<T> =
  T extends UseQuery<QueryDefinition<any, any, any, infer ResultType, any>> ? ResultType : never
export type ExtractRTKQueryOptions<T> = T extends (arg: any, options: infer Options) => any ? Options : never

export type RTKQueryResultType<T> = TypedUseQueryHookResult<
  ExtractRTKQueryReturnType<T>,
  ExtractRTKQueryArgs<T>,
  typeof baseQuery
>

type AutoCast<T> = T extends any ? T : never

export type RTKQueryHook<T> = (
  args?: Partial<ExtractRTKQueryArgs<T>>,
  options?: ExtractRTKQueryOptions<T>,
) => AutoCast<RTKQueryResultType<T>>

export const persistedApiReducer = persistScopedReducer(
  {
    key: 'api',
    storage: indexedDBStorage('api'),
    mergeFn: (inboundState, reducedState, hydrationContext: Record<string, number>) => {
      for (const [key, query] of Object.entries(inboundState.queries ?? {})) {
        if (!query) {
          continue
        }
        const queryWithPersist = query as QuerySubStateWithPersist
        if (
          queryWithPersist.hydrationVersion !== undefined &&
          query.endpointName &&
          query.endpointName in hydrationContext &&
          hydrationContext[query.endpointName] > queryWithPersist.hydrationVersion
        ) {
          delete inboundState.queries[key]
          continue
        }

        if (key in reducedState.queries) {
          if (
            reducedState.queries[key]?.status !== 'pending' &&
            reducedState.queries[key]?.status !== 'uninitialized'
          ) {
            // If the query is not pending or uninitialized, we can just replace it since it's already finished
            inboundState.queries[key] = reducedState.queries[key]
          } else {
            // If the query is pending, we need to keep the requestId so that the query fulfills correctly
            query.requestId = reducedState.queries[key]?.requestId
          }
        } else {
          inboundState.queries[key]!.startedTimeStamp = -1
        }
      }
      return autoMergeLevel2(inboundState, reducedState)
    },
  },
  baseApi.reducer,
)
